/**
 * Derive "plans from" figures for marketing pages from the live product
 * catalog (/api/products). Nothing here is hardcoded: when the catalog is
 * unavailable the caller omits the numeric claim rather than guessing.
 */

export interface CatalogPlan {
  monthlyPrice: number;
  annualTotal: number | null;
  billingCycle: string;
  isActive?: boolean;
}

export interface CatalogProduct {
  pricingPlans?: CatalogPlan[];
  isActive?: boolean;
}

export interface StartingPrices {
  /** Lowest month-to-month plan: the headline figure (an actual monthly payment). */
  monthly?: { monthlyPrice: number };
  /** Lowest annual plan, computed separately: effective monthly rate and the upfront total. */
  annual?: { monthlyPrice: number; annualTotal: number };
}

function isUsable(plan: CatalogPlan): boolean {
  return plan.isActive !== false && typeof plan.monthlyPrice === 'number' && Number.isFinite(plan.monthlyPrice) && plan.monthlyPrice > 0;
}

export function getStartingPrices(products: CatalogProduct[] | null | undefined): StartingPrices {
  const plans = (products || [])
    .filter(p => p.isActive !== false)
    .flatMap(p => p.pricingPlans || [])
    .filter(isUsable);

  const result: StartingPrices = {};

  const monthlyPlans = plans.filter(p => p.billingCycle === 'monthly');
  if (monthlyPlans.length > 0) {
    const lowest = monthlyPlans.reduce((a, b) => (b.monthlyPrice < a.monthlyPrice ? b : a));
    result.monthly = { monthlyPrice: lowest.monthlyPrice };
  }

  const annualPlans = plans.filter(
    p => p.billingCycle === 'annual' && typeof p.annualTotal === 'number' && Number.isFinite(p.annualTotal) && (p.annualTotal as number) > 0
  );
  if (annualPlans.length > 0) {
    const lowest = annualPlans.reduce((a, b) => (b.monthlyPrice < a.monthlyPrice ? b : a));
    result.annual = { monthlyPrice: lowest.monthlyPrice, annualTotal: lowest.annualTotal as number };
  }

  return result;
}

export function formatPrice(amount: number): string {
  return `$${amount.toFixed(2)}`;
}
